import { useState, useRef, useEffect } from "react";
import { C } from "./re-colors.ts";
import { BANK, AI_INSIGHTS } from "./re-data.ts";
import { Card } from "./re-components.tsx";
import { DocModal } from "./re-modals.tsx";

// ── AI INSIGHTS PANEL ─────────────────────────────────────────────
export function AIInsightsPanel({
  pid, onLog, persona,
}: {
  pid: string;
  onLog: (action: string, detail: string, category: string) => void;
  persona: { name: string; id: string };
}) {
  const [open, setOpen] = useState(false);
  const [expandedCats, setExpandedCats] = useState<Record<number, boolean>>({});
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<string | false>(false);
  const [aiResponses, setAiResponses] = useState<Record<string, string>>({});
  const [doc, setDoc] = useState<{ title: string; prompt: string } | null>(null);

  const insights = AI_INSIGHTS[pid] || [];
  const totalCount = insights.reduce((s, c) => s + c.insights.length, 0);
  const bankCtx = "Assets: $4.21B, NIM: 3.42%, Net Income: $7.9M, NPL: 0.61%, CRE Concentration: 287%, Deposits: $3.62B";

  async function deepDive(insight: { title: string; body: string }, key: string) {
    if (aiResponses[key]) {
      setExpandedInsights(p => ({ ...p, [key]: !p[key] }));
      return;
    }
    setLoading(key);
    setExpandedInsights(p => ({ ...p, [key]: true }));
    onLog("AI Deep Dive", insight.title, "AI Analysis");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 500,
          system: "You are a banking risk analyst for Demo Regional Bank. Provide a concise 3-bullet deep dive. Use specific numbers.",
          messages: [{ role: "user", content: `Deep dive for ${pid}: "${insight.title}". Context: ${insight.body}. Provide 3 specific actionable recommendations with timelines.` }],
        }),
      });
      const d = await res.json();
      setAiResponses(p => ({ ...p, [key]: d.content?.find((b: any) => b.type === "text")?.text || "Unable to generate." }));
    } catch {
      setAiResponses(p => ({ ...p, [key]: "Connection error." }));
    }
    setLoading(false);
  }

  function generateDoc(insight: { title: string; body: string }) {
    setDoc({ title: `Analysis: ${insight.title}`, prompt: `Generate a detailed banking memo for Demo Regional Bank regarding: "${insight.title}". Context: ${insight.body}. Include: executive summary, detailed analysis, recommended actions with owners and deadlines.` });
    onLog("Generated analysis memo", insight.title, "Document");
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, background: `linear-gradient(135deg,${C.aiPurple}18,${C.aiPurple}08)`, border: `1.5px solid ${C.aiPurple}35`, borderRadius: open ? "12px 12px 0 0" : 12, padding: "14px 18px", cursor: "pointer" }}
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg,${C.aiPurple},#8B5CF6)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: `0 0 12px ${C.aiPurple}40` }}>🧠</div>
          <div style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: "50%", background: "#4ade80", border: "2px solid #fff" }} />
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: C.aiPurple }}>AI Intelligence Panel</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: `${C.aiPurple}20`, color: C.aiPurple, fontWeight: 700, border: `1px solid ${C.aiPurple}30` }}>{totalCount} insights</span>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: C.goldBg, color: C.gold, fontWeight: 700, border: `1px solid ${C.gold}30` }}>Live · {BANK.asOfDate}</span>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Market signals · Predictive analytics · Regulatory intelligence · Deep dive on demand</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{open ? "Collapse" : "Expand"}</span>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: `${C.aiPurple}15`, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform .25s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 4l4 4 4-4" stroke={C.aiPurple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </button>

      {open && (
        <div style={{ border: `1.5px solid ${C.aiPurple}35`, borderTop: "none", borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
          {insights.map((cat, ci) => (
            <div key={ci} style={{ borderBottom: ci < insights.length - 1 ? `1px solid ${C.aiPurple}20` : "none" }}>
              <button
                onClick={() => setExpandedCats(p => ({ ...p, [ci]: !p[ci] }))}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 18px", background: expandedCats[ci] ? `${cat.color}08` : C.surface, border: "none", cursor: "pointer" }}
              >
                <span style={{ fontSize: 16 }}>{cat.icon}</span>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 12, color: cat.color, textAlign: "left" }}>{cat.category}</span>
                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: `${cat.color}15`, color: cat.color, fontWeight: 700 }}>{cat.insights.length}</span>
                <svg style={{ marginLeft: 8, transition: "transform .2s", transform: expandedCats[ci] ? "rotate(180deg)" : "rotate(0deg)" }} width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4l4 4 4-4" stroke={cat.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {expandedCats[ci] && (
                <div style={{ background: C.card }}>
                  {cat.insights.map((insight, ii) => {
                    const key = `${ci}-${ii}`;
                    const isExpanded = expandedInsights[key];
                    const isLoadingThis = loading === key;
                    return (
                      <div key={ii} style={{ borderTop: `1px solid ${C.border}`, margin: "0 18px" }}>
                        <div style={{ padding: "12px 0 8px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <div style={{ width: 2, background: cat.color, borderRadius: 2, alignSelf: "stretch", flexShrink: 0, marginTop: 2 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10, background: `${cat.color}15`, color: cat.color, letterSpacing: .4, textTransform: "uppercase" }}>{insight.tag}</span>
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.4, marginBottom: 5 }}>{insight.title}</div>
                              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{insight.body}</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
                                <span style={{ fontSize: 9, color: C.dim }}>🔗 SOURCE: {insight.src}</span>
                              </div>
                              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                                <button
                                  onClick={() => deepDive(insight, key)}
                                  style={{ display: "flex", alignItems: "center", gap: 6, background: isExpanded ? `${C.aiPurple}15` : "transparent", border: `1px solid ${C.aiPurple}40`, borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 11, color: C.aiPurple, fontWeight: 700 }}
                                >
                                  {isLoadingThis
                                    ? [0, 1, 2].map(i => <span key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: C.aiPurple, display: "inline-block", animation: `dot .9s ${i * .2}s infinite` }} />)
                                    : isExpanded ? "▲ Hide" : "✦ Deep Dive"}
                                </button>
                                <button
                                  onClick={() => generateDoc(insight)}
                                  style={{ display: "flex", alignItems: "center", gap: 5, background: "transparent", border: `1px solid ${C.gold}40`, borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 11, color: C.gold, fontWeight: 700 }}
                                >
                                  📄 Generate Memo
                                </button>
                              </div>
                              {isExpanded && aiResponses[key] && (
                                <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 10, background: `${C.aiPurple}06`, border: `1px solid ${C.aiPurple}20` }}>
                                  <div style={{ fontSize: 10, fontWeight: 800, color: C.aiPurple, letterSpacing: .8, textTransform: "uppercase", marginBottom: 8 }}>✦ AI Deep Dive</div>
                                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiResponses[key]}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <div style={{ padding: "10px 18px", background: `${C.aiPurple}06`, borderTop: `1px solid ${C.aiPurple}20`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, color: C.aiPurple, fontWeight: 700 }}>✦ AI Intelligence</span>
            <span style={{ fontSize: 9, color: C.dim }}>· Powered by Claude · Live market data · Regulatory feeds</span>
          </div>
        </div>
      )}
      {doc && <DocModal title={doc.title} personaName={persona.name} prompt={doc.prompt} bankCtx={bankCtx} onClose={() => setDoc(null)} />}
    </div>
  );
}

// ── CHAT PANEL ────────────────────────────────────────────────────
const CHAT_SUGGESTIONS: Record<string, string[]> = {
  ceo:        ["Biggest risk today?", "SBA pipeline summary", "Board package"],
  cfo:        ["NIM if Fed cuts 25bps", "Q1 close status", "Generate ALCO memo"],
  banker:     ["At-risk clients", "Cross-sell this week", "Draft renewal letter for Martinez"],
  ops:        ["ACH exceptions today", "Wire summary", "Branch scorecard"],
  compliance: ["SAR candidates", "CRA gaps", "Draft SAR narrative for #4471"],
  credit:     ["Criticized loans", "Ag drought exposure", "Downgrade memo for Valley Ridge Growers"],
};

export function ChatPanel({
  persona, onLog,
}: {
  persona: { name: string; full: string; id: string; icon: string; color: string; grad: string };
  onLog: (action: string, detail: string, category: string) => void;
}) {
  const [msgs, setMsgs] = useState([{
    role: "ai",
    text: `Good morning, ${persona.name.split(" ")[0]}. I'm your AI Agent with live access to all bank systems. How can I help?`,
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [docPrompt, setDocPrompt] = useState<{ title: string; prompt: string } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function send(txt?: string) {
    const msg = txt || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMsgs(m => [...m, { role: "user", text: msg }]);
    setLoading(true);
    onLog("AI Chat", msg.substring(0, 50), "AI Chat");

    if (/draft|generate|memo|letter/i.test(msg)) {
      setDocPrompt({ title: `AI Generated: ${msg.substring(0, 40)}…`, prompt: `${msg}\n\nContext: Demo Regional Bank, Assets $4.21B, Metro City, March 2026.` });
      setMsgs(m => [...m, { role: "ai", text: "Document generator opened with your request." }]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are an AI banking agent for Demo Regional Bank ($4.21B, Metro City) serving ${persona.name}, ${persona.full}. NIM: 3.42%, Net Income: $7.9M, NPL: 0.61%, CRE Conc: 287%, Deposits: $3.62B. Give concise answers with specific numbers. Under 160 words. Use • for bullets.`,
          messages: [{ role: "user", content: msg }],
        }),
      });
      const d = await res.json();
      setMsgs(m => [...m, { role: "ai", text: d.content?.find((b: any) => b.type === "text")?.text || "Unable to respond." }]);
    } catch {
      setMsgs(m => [...m, { role: "ai", text: "Connection error." }]);
    }
    setLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.card, borderLeft: `1px solid ${C.border}` }}>
      {/* Header */}
      <div style={{ padding: "13px 15px", borderBottom: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: persona.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{persona.icon}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{persona.name}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{persona.full}</div>
            <div style={{ fontSize: 11, color: C.forest, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.forest, display: "inline-block" }} />
              All systems live
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: 13, display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "85%",
              background: m.role === "user" ? persona.color : C.surface,
              color: m.role === "user" ? "#fff" : C.text,
              borderRadius: m.role === "user" ? "13px 13px 3px 13px" : "13px 13px 13px 3px",
              padding: "10px 13px", fontSize: 12, lineHeight: 1.6,
              border: m.role === "ai" ? `1px solid ${C.border}` : "none",
              whiteSpace: "pre-wrap",
            }}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 4, padding: "10px 13px", background: C.surface, borderRadius: 12, width: 54, border: `1px solid ${C.border}` }}>
            {[0, 1, 2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: C.muted, animation: `dot .9s ${i * .2}s infinite` }} />)}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "10px 13px", borderTop: `1px solid ${C.border}`, background: C.surface }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
          {(CHAT_SUGGESTIONS[persona.id] || []).map((s, i) => (
            <button key={i} onClick={() => send(s)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${C.border}`, background: C.card, cursor: "pointer", color: C.muted }}>{s}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask or say 'draft…' to generate a document…"
            style={{ flex: 1, padding: "9px 13px", borderRadius: 22, border: `1px solid ${C.border}`, fontSize: 12, outline: "none", background: C.card, color: C.text }}
            onFocus={e => (e.target.style.borderColor = persona.color)}
            onBlur={e => (e.target.style.borderColor = C.border)}
          />
          <button onClick={() => send()} style={{ background: persona.grad, color: "#fff", border: "none", borderRadius: 22, padding: "9px 15px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>↑</button>
        </div>
      </div>
      {docPrompt && <DocModal title={docPrompt.title} personaName={persona.name} prompt={docPrompt.prompt} bankCtx="" onClose={() => setDocPrompt(null)} />}
    </div>
  );
}
